// src/pages/personalizados/GerencialPersonalizedPage.jsx
import { useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Stack,
    TextField,
    Typography,
    IconButton,
    Dialog
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import Swal from "sweetalert2";

import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";


import {
    getManagerPersonalizedOrderById,
    getManagerPersonalizedOrders,
    getManagerPersonalizedOrderPaymentSummary,
    updateManagerPersonalizedOrder,
} from "./managerPersonalized.service";

import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";


import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useNavigate } from "react-router-dom";

const SELLER_STATUS_OPTIONS = [
    { value: "CREATED", label: "Criado" },
    { value: "CONFIRMED", label: "Confirmado" },
    { value: "READY", label: "Pronto" },
    { value: "CANCELED", label: "Cancelado" },
];

const PITAYA_STATUS_OPTIONS = [
    { value: "CREATED", label: "Criado" },
    { value: "CONFIRMED", label: "Confirmado" },
    { value: "CANCELED", label: "Cancelado" },
    { value: "PRODUCTION_RAW_1", label: "Produção Bruto 1" },
    { value: "PRODUCTION_BATH_1", label: "Produção Banho 1" },
    { value: "PRODUCTION_RAW_2", label: "Produção Bruto 2" },
    { value: "PRODUCTION_BATH_2", label: "Produção Banho 2" },
    { value: "IN_TRANSPORT", label: "Em Transporte" },
    { value: "PITAYA_PRODUCTION", label: "Pitaya Produção" },
    { value: "READY", label: "Pronto" },
];

const DEFAULT_SELLER_STATUS = [];
const DEFAULT_PITAYA_STATUS = [];

const PITAYA_TO_SELLER_STATUS = {
    CREATED: "CREATED",
    CONFIRMED: "CONFIRMED",
    CANCELED: "CANCELED",
    PRODUCTION_RAW_1: "CONFIRMED",
    PRODUCTION_BATH_1: "CONFIRMED",
    PRODUCTION_RAW_2: "CONFIRMED",
    PRODUCTION_BATH_2: "CONFIRMED",
    IN_TRANSPORT: "CONFIRMED",
    PITAYA_PRODUCTION: "CONFIRMED",
    READY: "READY",
};

const SELLER_TO_PITAYA_STATUS = {
    CREATED: ["CREATED"],
    CONFIRMED: [
        "CONFIRMED",
        "PRODUCTION_RAW_1",
        "PRODUCTION_BATH_1",
        "PRODUCTION_RAW_2",
        "PRODUCTION_BATH_2",
        "IN_TRANSPORT",
        "PITAYA_PRODUCTION",
    ],
    READY: ["READY"],
    CANCELED: ["CANCELED"],
};

function uniqueArray(values = []) {
    return [...new Set(values.filter(Boolean))];
}

function deriveSellerStatusesFromPitaya(pitayaList = []) {
    if (!pitayaList.length) {
        return SELLER_STATUS_OPTIONS.map((item) => item.value);
    }
    return uniqueArray(pitayaList.map((status) => PITAYA_TO_SELLER_STATUS[status]));
}

function derivePitayaStatusesFromSeller(sellerList = []) {
    if (!sellerList.length) {
        return PITAYA_STATUS_OPTIONS.map((item) => item.value);
    }
    return uniqueArray(
        sellerList.flatMap((status) => SELLER_TO_PITAYA_STATUS[status] || [])
    );
}

function getItemCode(item) {
    return (
        item?.product_code ||
        item?.item_code ||
        item?.pitaya_code ||
        item?.code ||
        item?.sku ||
        "-"
    );
}

const darkFieldSx = {
    "& .MuiOutlinedInput-root": {
        color: "#FFF4F8",
        background:
            "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
        "& fieldset": {
            borderColor: "rgba(255,255,255,0.12)",
        },
        "&:hover fieldset": {
            borderColor: "rgba(243,166,195,0.28)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#F3A6C3",
        },
    },
    "& .MuiInputLabel-root": {
        color: "rgba(255,220,232,0.78)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#F3A6C3",
    },
    "& .MuiInputBase-input::placeholder": {
        color: "rgba(255,220,232,0.46)",
        opacity: 1,
    },
    "& .MuiNativeSelect-select": {
        color: "#FFF4F8",
    },
};

function formatBRL(value) {
    return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

function formatDateTime(value) {
    if (!value) return "-";
    const dt = new Date(value);
    return dt.toLocaleString("pt-BR");
}

function getStatusChipColor(status) {
    switch (status) {
        case "READY":
            return "success";
        case "CANCELED":
            return "error";
        case "CONFIRMED":
            return "info";
        default:
            return "warning";
    }
}

function getReadableFieldLabel(key) {
    const dict = {
        name: "Nome",
        label: "Campo",
        title: "Título",
        text: "Texto",
        value: "Valor",
        content: "Conteúdo",
        field: "Campo",
        type: "Tipo",
        color: "Cor",
        size: "Tamanho",
        model: "Modelo",
        phrase: "Frase",
        initials: "Iniciais",
        letter: "Letra",
        number: "Número",
        custom_text: "Texto personalizado",
        customText: "Texto personalizado",
        selected_extra_option: "Opção extra",
        selectedExtraOption: "Opção extra",
        option: "Opção",
        option_name: "Opção",
        optionName: "Opção",
        font: "Fonte",
        position: "Posição",
    };
    return dict[key] || key;
}

function normalizeCustomFields(data) {
    if (!data) return [];

    if (Array.isArray(data)) {
        return data
            .map((entry, index) => {
                if (entry == null) return null;

                if (typeof entry === "string" || typeof entry === "number") {
                    return {
                        id: `simple-${index}`,
                        label: `Detalhe ${index + 1}`,
                        value: String(entry),
                    };
                }

                if (typeof entry === "object") {
                    const label =
                        entry.label ||
                        entry.name ||
                        entry.title ||
                        entry.field ||
                        `Detalhe ${index + 1}`;

                    const value =
                        entry.value ??
                        entry.text ??
                        entry.content ??
                        entry.selected ??
                        entry.option ??
                        entry.description ??
                        "";

                    if (
                        value !== "" &&
                        value !== null &&
                        value !== undefined &&
                        typeof value !== "object"
                    ) {
                        return {
                            id: `obj-${index}`,
                            label: String(label),
                            value: String(value),
                        };
                    }

                    const flattened = Object.entries(entry)
                        .filter(([key, val]) => {
                            if (
                                ["id", "created_at", "updated_at"].includes(key) ||
                                val === null ||
                                val === undefined ||
                                val === ""
                            ) {
                                return false;
                            }
                            return typeof val !== "object";
                        })
                        .map(([key, val], subIndex) => ({
                            id: `obj-${index}-${subIndex}`,
                            label: getReadableFieldLabel(key),
                            value: String(val),
                        }));

                    return flattened.length
                        ? {
                            id: `group-${index}`,
                            groupLabel: String(label),
                            items: flattened,
                        }
                        : null;
                }

                return null;
            })
            .filter(Boolean);
    }

    if (typeof data === "object") {
        return Object.entries(data)
            .filter(
                ([, value]) =>
                    value !== null &&
                    value !== undefined &&
                    value !== "" &&
                    typeof value !== "object"
            )
            .map(([key, value], index) => ({
                id: `root-${index}`,
                label: getReadableFieldLabel(key),
                value: String(value),
            }));
    }

    return [];
}

function buildFirebaseProductImageUrl(fileName) {
    if (!fileName) return "";

    const cleanName = String(fileName).trim().replace(/^\/+/, "");
    const fullPath = `productapp/${cleanName}`;
    const encodedPath = encodeURIComponent(fullPath);
    console.log('encodedhP', encodedPath)

    return `https://firebasestorage.googleapis.com/v0/b/pitaya-pay-ebd8c.appspot.com/o/${encodedPath}?alt=media`;
}

function resolvePitayaProductImageUrl(item = {}) {
    if (!item?.code) return "";
    return buildFirebaseProductImageUrl(`${item.code}.jpg`);
}

function resolveUploadedPhotoUrl(item = {}) {
    return item?.photo_url || "";
}

function formatMoneyBR(value) {
    const number = Number(value || 0);
    return number.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

function escapeHtml(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}


function normalizeCustomTexts(customTexts) {
    if (!Array.isArray(customTexts)) return [];

    return customTexts
        .map((entry) => {
            if (!entry) return null;

            if (typeof entry === "string") {
                return {
                    label: "Campo",
                    value: entry,
                };
            }

            if (typeof entry === "object") {
                return {
                    label:
                        entry.label ||
                        entry.name ||
                        entry.field ||
                        entry.key ||
                        "Campo",
                    value:
                        entry.value ||
                        entry.text ||
                        entry.content ||
                        "",
                };
            }

            return null;
        })
        .filter(Boolean)
        .filter((entry) => String(entry.value || "").trim());
}

function getItemSupplierMeta(item = {}) {
    const raw = item?.raw_item_payload || {};
    const catalog = item?.catalog_product_data || {};

    const supplier =
        item?.supplier ||
        catalog?.supplier ||
        raw?.supplier ||
        raw?.supplier_name ||
        "Fornecedor não identificado";

    const supplierCode =
        item?.supplier_code ||
        catalog?.supplier_code ||
        raw?.supplier_code ||
        raw?.codigo_fornecedor ||
        raw?.fornecedor_codigo ||
        "SEM_CODIGO";

    return {
        supplier,
        supplierCode,
        key: `${supplierCode}__${supplier}`,
    };
}

function groupItemsBySupplier(items = []) {
    return items.reduce((acc, item) => {
        const meta = getItemSupplierMeta(item);

        if (!acc[meta.key]) {
            acc[meta.key] = {
                supplier: meta.supplier,
                supplierCode: meta.supplierCode,
                items: [],
            };
        }

        acc[meta.key].items.push(item);
        return acc;
    }, {});
}
function getItemPrintPosition(items = [], targetItem) {
    const sameCodeItems = items.filter(
        (item) => getItemCode(item) === getItemCode(targetItem)
    );

    const index = sameCodeItems.findIndex(
        (item) => item.id === targetItem.id
    );

    return {
        position: index >= 0 ? index + 1 : 1,
        total: sameCodeItems.length,
        totalQuantity: sameCodeItems.reduce(
            (sum, item) => sum + Number(item.quantity || 1),
            0
        ),
        repeated: sameCodeItems.length > 1,
    };
}

function buildItemPrintHtml({
    order,
    item,
    includeSupplierHeader = true,
    itemPosition = null,
}) {
    const customTexts = normalizeCustomTexts(item?.custom_texts);
    const pitayaProductImageUrl = resolvePitayaProductImageUrl(item);
    const uploadedPhotoUrl = resolveUploadedPhotoUrl(item);
    const supplierMeta = getItemSupplierMeta(item);

    return `
        <section style="page-break-inside: avoid; margin-bottom: 24px; border: 1px solid #ddd; border-radius: 12px; padding: 18px;">
            <div style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 16px;
                border-bottom: 1px solid #E5E7EB;
                padding-bottom: 12px;
                margin-bottom: 16px;
            ">
                <div style="flex: 1; padding-right: 16px;">
                    <div style="font-size: 16px; font-weight: 700; margin-bottom: 4px; color: #902348;">
                        Pitaya
                    </div>

                    <div style="font-size: 9px; line-height: 1.4; color: #4B5563;">
                        Joias personalizadas
                    </div>

                    <div style="font-size: 9px; line-height: 1.4; color: #4B5563;">
                        WhatsApp: (51) 99870-3296
                    </div>

                    <div style="font-size: 9px; line-height: 1.4; color: #4B5563;">
                        Site: www.pitayajoias.com.br/
                    </div>
                </div>

                <div style="
                    width: 120px;
                    height: 80px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                ">
                    <img
                        src="/Logo-rosa.png"
                        alt="Pitaya"
                        style="max-width: 120px; max-height: 80px; object-fit: contain;"
                    />
                </div>
            </div>

            ${includeSupplierHeader ? `
                <div style="margin-bottom: 14px;">
                    <div style="font-size: 12px; color: #666;">Fornecedor</div>
                    <div style="font-size: 18px; font-weight: 700;">${escapeHtml(supplierMeta.supplier)}</div>
                    <div style="font-size: 13px; color: #444;">Código Fornecedor: ${escapeHtml(supplierMeta.supplierCode)}</div>
                </div>
            ` : ""}

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px;">
                <div>
                    <div style="font-size: 12px; color: #666;">Pedido</div>
                    <div style="font-size: 15px; font-weight: 700;">${escapeHtml(order?.order_code || "-")}</div>
                </div>
                <div>
                    <div style="font-size: 12px; color: #666;">Data</div>
                    <div style="font-size: 15px; font-weight: 700;">${escapeHtml(formatDateTime(order?.created_at))}</div>
                </div>
            </div>

            <div style="border-top: 1px solid #eee; padding-top: 14px;">

                <div style="font-size: 18px; font-weight: 800; margin-bottom: 8px;">
                    Item: ${escapeHtml(
                        item?.product_name ||
                        item?.catalog_product_name ||
                        item?.description ||
                        "-"
                    )}
                </div>

                ${itemPosition?.repeated ? `
                    <div style="
                        display: inline-block;
                        padding: 7px 12px;
                        margin-bottom: 14px;
                        background: #FCE7EF;
                        border: 1px solid #E8A8C0;
                        border-radius: 8px;
                        color: #902348;
                        font-size: 13px;
                        font-weight: 800;
                    ">
                        PERSONALIZAÇÃO ${itemPosition.position} DE ${itemPosition.total}
                        — ${escapeHtml(getItemCode(item))}
                    </div>

                    <div style="
                        font-size: 12px;
                        color: #555;
                        margin-bottom: 12px;
                    ">
                        Total deste código no pedido:
                        <strong>${itemPosition.totalQuantity} unidade(s)</strong>
                    </div>
                ` : ""}

                <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 14px;">
                    <div>
                        <div style="font-size: 12px; color: #666;">Código do item</div>
                        <div style="font-size: 14px; font-weight: 700;">${escapeHtml(item?.code || "-")}</div>
                    </div>
                    <div>
                        <div style="font-size: 12px; color: #666;">Quantidade</div>
                        <div style="font-size: 14px; font-weight: 700;">${escapeHtml(item?.quantity || 1)}</div>
                    </div>
                    <div>
                        <div style="font-size: 12px; color: #666;">Tipo</div>
                        <div style="font-size: 14px; font-weight: 700;">${escapeHtml(item?.product_type || "-")}</div>
                    </div>
                    <div>
                        <div style="font-size: 12px; color: #666;">Cor</div>
                        <div style="font-size: 14px; font-weight: 700;">${escapeHtml(item?.color || "-")}</div>
                    </div>
                    <div>
                        <div style="font-size: 12px; color: #666;">Produto catálogo</div>
                        <div style="font-size: 14px; font-weight: 700;">${escapeHtml(item?.catalog_product_name || "-")}</div>
                    </div>
                </div>

                ${customTexts.length
            ? `
                    <div style="margin-bottom: 12px;">
                        <div style="font-size: 12px; color: #666; margin-bottom: 6px;">Campos personalizados</div>
                        ${customTexts
                .map(
                    (entry) => `
                                <div style="font-size: 14px; margin-bottom: 4px;">
                                    <strong>${escapeHtml(entry.label)}:</strong> ${escapeHtml(entry.value)}
                                </div>
                            `
                )
                .join("")}
                    </div>
                `
            : ""
        }


                ${(pitayaProductImageUrl || uploadedPhotoUrl)
            ? `
                    <div style="margin-top: 18px; display: grid; grid-template-columns: 1fr 1fr; gap: 18px; align-items: start;">
                        ${pitayaProductImageUrl ? `
                            <div>
                                <div style="font-size: 12px; color: #666; margin-bottom: 8px;">Imagem do produto Pitaya</div>
                                <img
                                    src="${pitayaProductImageUrl}"
                                    alt="Imagem do produto Pitaya"
                                    crossorigin="anonymous"
                                    referrerpolicy="no-referrer"
                                    style="max-width: 100%; max-height: 320px; border-radius: 12px; border: 1px solid #ddd;"
                                />
                            </div>
                        ` : ""}

                        ${uploadedPhotoUrl ? `
                            <div>
                                <div style="font-size: 12px; color: #666; margin-bottom: 8px;">Foto enviada pela cliente</div>
                                <img
                                    src="${uploadedPhotoUrl}"
                                    alt="Foto enviada pela cliente"
                                    crossorigin="anonymous"
                                    referrerpolicy="no-referrer"
                                    style="max-width: 100%; max-height: 320px; border-radius: 12px; border: 1px solid #ddd;"
                                />
                            </div>
                        ` : ""}
                    </div>
                `
            : ""
        }
            </div>
        </section>
    `;
}


function openPrintWindow({ title, bodyHtml }) {
    const printWindow = window.open("", "_blank", "width=1100,height=900");

    if (!printWindow) {
        Swal.fire({
            icon: "warning",
            title: "Popup bloqueado",
            text: "Libere popups do navegador para gerar o PDF.",
            confirmButtonColor: "#902348",
        });
        return;
    }

    printWindow.document.write(`
		<html>
			<head>
				<title>${escapeHtml(title)}</title>
				<meta charset="utf-8" />
				<style>
					@page { size: A4; margin: 16mm; }

					body {
						font-family: Arial, sans-serif;
						color: #222;
						margin: 0;
						padding: 24px;
						background: #f5f5f5;
					}

					.container {
						max-width: 900px;
						margin: 0 auto;
						background: white;
						padding: 24px;
						border-radius: 12px;
						box-shadow: 0 10px 30px rgba(0,0,0,0.08);
					}

					.page-title {
						font-size: 26px;
						font-weight: 800;
						margin-bottom: 8px;
					}

					.page-subtitle {
						font-size: 13px;
						color: #666;
						margin-bottom: 24px;
					}

					.toolbar {
						display: flex;
						justify-content: flex-end;
						margin-bottom: 16px;
					}

					.print-btn {
						background: #902348;
						color: white;
						border: none;
						padding: 10px 16px;
						border-radius: 8px;
						cursor: pointer;
						font-weight: 600;
					}

					.print-btn:hover {
						background: #6e1b38;
					}

					img {
						display: block;
					}

					@media print {
						body {
							background: white;
							padding: 0;
						}

						.toolbar {
							display: none;
						}

						.container {
							box-shadow: none;
							padding: 0;
						}
                        .supplier-print-group {
                            break-after: page;
                            page-break-after: always;
                        }

                        .supplier-print-group:last-child {
                            break-after: auto;
                            page-break-after: auto;
                        }

                        .supplier-print-group > section {
                            break-inside: avoid;
                            page-break-inside: avoid;
                        }
					}
				</style>
			</head>

			<body>
				<div class="container">
					<div class="toolbar">
						<button class="print-btn" onclick="window.print()">
							Imprimir / Salvar PDF
						</button>
					</div>

					<div class="page-title">${escapeHtml(title)}</div>

					${bodyHtml}
				</div>
			</body>
		</html>
	`);

    printWindow.document.close();
}


function DetailsFieldsCard({ data }) {
    const fields = normalizeCustomFields(data);

    if (!fields.length) return null;

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                border: "1px solid rgba(243,166,195,0.14)",
                background:
                    "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.06) 100%)",
                boxShadow: "none",
            }}
        >
            <CardContent sx={{ p: 1.6, "&:last-child": { pb: 1.6 } }}>
                <Typography
                    fontWeight={800}
                    mb={1.2}
                    sx={{ color: "#FFBDD7", fontSize: "0.95rem" }}
                >
                    Detalhes da personalização
                </Typography>

                <Stack spacing={1}>
                    {fields.map((field) => {
                        if (field.items?.length) {
                            return (
                                <Card
                                    key={field.id}
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2.5,
                                        border: "1px solid rgba(243,166,195,0.12)",
                                        background:
                                            "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 100%)",
                                        boxShadow: "none",
                                    }}
                                >
                                    <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                                        <Typography
                                            fontWeight={700}
                                            sx={{ color: "#F6B3CD", mb: 1, fontSize: "0.9rem" }}
                                        >
                                            {field.groupLabel}
                                        </Typography>

                                        <Stack spacing={0.8}>
                                            {field.items.map((sub) => (
                                                <Box
                                                    key={sub.id}
                                                    sx={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        gap: 0.75,
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: "#FFD6E6",
                                                            fontSize: "0.86rem",
                                                        }}
                                                    >
                                                        {sub.label}:
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            color: "rgba(255,240,246,0.88)",
                                                            fontSize: "0.88rem",
                                                        }}
                                                    >
                                                        {sub.value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        }

                        return (
                            <Card
                                key={field.id}
                                variant="outlined"
                                sx={{
                                    borderRadius: 2.5,
                                    border: "1px solid rgba(243,166,195,0.12)",
                                    background:
                                        "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 100%)",
                                    boxShadow: "none",
                                }}
                            >
                                <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            color: "#FFD6E6",
                                            fontSize: "0.84rem",
                                            mb: 0.4,
                                        }}
                                    >
                                        {field.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: "rgba(255,240,246,0.92)",
                                            fontSize: "0.92rem",
                                        }}
                                    >
                                        {field.value}
                                    </Typography>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>
            </CardContent>
        </Card>
    );
}

function DetailInfoCard({ icon, title, value, subtitle }) {
    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.08)",
                background:
                    "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
                height: "100%",
                boxShadow: "none",
            }}
        >
            <CardContent sx={{ p: 1.6, "&:last-child": { pb: 1.6 } }}>
                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            minWidth: 36,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                                "linear-gradient(135deg, rgba(161,44,88,0.28) 0%, rgba(122,33,68,0.45) 100%)",
                            color: "#FFD6E6",
                        }}
                    >
                        {icon}
                    </Box>

                    <Box>
                        <Typography
                            sx={{
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                color: "#F3A6C3",
                                mb: 0.2,
                            }}
                        >
                            {title}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: "0.95rem",
                                fontWeight: 700,
                                color: "#FFF2F7",
                            }}
                        >
                            {value || "-"}
                        </Typography>

                        {!!subtitle && (
                            <Typography
                                sx={{
                                    fontSize: "0.82rem",
                                    color: "rgba(255,220,232,0.72)",
                                    mt: 0.35,
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

function ItemCodesCard({ items = [] }) {
    const codes = uniqueArray(
        items.map((item) => getItemCode(item)).filter((code) => code && code !== "-")
    );

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.08)",
                background:
                    "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
                height: "100%",
                boxShadow: "none",
            }}
        >
            <CardContent sx={{ p: 1.6, "&:last-child": { pb: 1.6 } }}>
                <Stack spacing={1}>
                    <Stack direction="row" spacing={1.2} alignItems="flex-start">
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                minWidth: 36,
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                    "linear-gradient(135deg, rgba(161,44,88,0.28) 0%, rgba(122,33,68,0.45) 100%)",
                                color: "#FFD6E6",
                            }}
                        >
                            <Inventory2OutlinedIcon fontSize="small" />
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: "0.8rem",
                                    fontWeight: 700,
                                    color: "#F3A6C3",
                                    mb: 0.2,
                                }}
                            >
                                Códigos dos itens
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "0.82rem",
                                    color: "rgba(255,220,232,0.72)",
                                    mb: 0.8,
                                }}
                            >
                                {codes.length} código(s)
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                        {codes.length ? (
                            codes.map((code) => (
                                <Chip
                                    key={code}
                                    size="small"
                                    label={code}
                                    sx={{
                                        fontWeight: 700,
                                        color: "#FFF1F6",
                                        border: "1px solid rgba(255,255,255,0.10)",
                                        background:
                                            "linear-gradient(135deg, rgba(161,44,88,0.35) 0%, rgba(122,33,68,0.55) 100%)",
                                    }}
                                />
                            ))
                        ) : (
                            <Typography
                                sx={{
                                    fontSize: "0.88rem",
                                    color: "rgba(255,220,232,0.82)",
                                }}
                            >
                                Sem código informado
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

function PaymentSummaryCard({ detail, paymentSummary }) {
    const orderTotal = Number(detail?.total || 0);
    const totalCharged = Number(paymentSummary?.total_charged || 0);
    const totalPaid = Number(paymentSummary?.total_paid || 0);
    const balance = Math.max(
        Number(paymentSummary?.payment_balance ?? orderTotal - totalPaid),
        0
    );
    const transactionsCount = Number(paymentSummary?.transactions_count || 0);
    const paidCount = Number(paymentSummary?.paid_count || 0);

    const metrics = [
        {
            label: "Pedido",
            value: formatMoneyBR(orderTotal),
            icon: <ShoppingBagOutlinedIcon sx={{ fontSize: 16 }} />,
            bg: "linear-gradient(135deg, rgba(157,78,221,0.20) 0%, rgba(109,40,217,0.34) 100%)",
            color: "#E9D5FF",
        },
        {
            label: "Cobrado",
            value: formatMoneyBR(totalCharged),
            icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />,
            bg: "linear-gradient(135deg, rgba(59,130,246,0.20) 0%, rgba(29,78,216,0.34) 100%)",
            color: "#DBEAFE",
        },
        {
            label: "Recebido",
            value: formatMoneyBR(totalPaid),
            icon: <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 16 }} />,
            bg: "linear-gradient(135deg, rgba(16,185,129,0.22) 0%, rgba(5,150,105,0.34) 100%)",
            color: "#D1FAE5",
        },
        {
            label: "Saldo",
            value: formatMoneyBR(balance),
            icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 16 }} />,
            bg: "linear-gradient(135deg, rgba(244,114,182,0.20) 0%, rgba(190,24,93,0.34) 100%)",
            color: "#FCE7F3",
        },
        {
            label: "Cobranças",
            value: transactionsCount,
            icon: <PaymentsOutlinedIcon sx={{ fontSize: 16 }} />,
            bg: "linear-gradient(135deg, rgba(251,191,36,0.20) 0%, rgba(217,119,6,0.34) 100%)",
            color: "#FEF3C7",
        },
        {
            label: "Pagas",
            value: paidCount,
            icon: <TaskAltOutlinedIcon sx={{ fontSize: 16 }} />,
            bg: "linear-gradient(135deg, rgba(34,197,94,0.20) 0%, rgba(22,163,74,0.34) 100%)",
            color: "#DCFCE7",
        },
    ];

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.08)",
                background:
                    "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
                height: "100%",
                boxShadow: "none",
            }}
        >
            <CardContent sx={{ p: 1.6, "&:last-child": { pb: 1.6 } }}>
                <Stack spacing={1.4}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <Box
                            sx={{
                                width: 34,
                                height: 34,
                                minWidth: 34,
                                borderRadius: "11px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                    "linear-gradient(135deg, rgba(26,170,122,0.28) 0%, rgba(18,111,84,0.45) 100%)",
                                color: "#D4FFEC",
                            }}
                        >
                            <PaymentsOutlinedIcon fontSize="small" />
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: "0.82rem",
                                    fontWeight: 800,
                                    color: "#9FF0C9",
                                    lineHeight: 1.1,
                                }}
                            >
                                Situação do pagamento
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "0.76rem",
                                    color: "rgba(222,255,240,0.72)",
                                    mt: 0.25,
                                }}
                            >
                                Resumo financeiro do pedido
                            </Typography>
                        </Box>
                    </Stack>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "repeat(2, minmax(0, 1fr))",
                                sm: "repeat(3, minmax(0, 1fr))",
                                md: "repeat(3, minmax(0, 1fr))",
                            },
                            gap: 1,
                        }}
                    >
                        {metrics.map((metric) => (
                            <Box
                                key={metric.label}
                                sx={{
                                    minWidth: 0,
                                    p: 1.1,
                                    borderRadius: 2.2,
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    background: "rgba(255,255,255,0.03)",
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ gap: 1 }}
                                >
                                    {/* esquerda: ícone + valor */}
                                    <Stack direction="row" spacing={0.9} alignItems="center" sx={{ minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                minWidth: 28,
                                                borderRadius: "9px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: metric.bg,
                                                color: metric.color,
                                            }}
                                        >
                                            {metric.icon}
                                        </Box>

                                        <Typography
                                            sx={{
                                                fontSize: "0.7rem",
                                                fontWeight: 700,
                                                color: "rgba(255,241,246,0.65)",
                                                whiteSpace: "nowrap",
                                                ml: 1,
                                            }}
                                        >
                                            {metric.label}
                                        </Typography>
                                    </Stack>

                                    {/* direita: label */}
                                    <Typography
                                        sx={{
                                            fontSize: "0.9rem",
                                            fontWeight: 800,
                                            color: "#FFF1F6",
                                            lineHeight: 1.1,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {metric.value}
                                    </Typography>
                                </Stack>
                            </Box>
                        ))}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}


export default function GerencialPersonalizedPage() {
    const [loadingList, setLoadingList] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [saving, setSaving] = useState(false);

    const [orders, setOrders] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);

    const [paymentSummary, setPaymentSummary] = useState(null);
    const [previewImage, setPreviewImage] = useState("");

    const navigate = useNavigate();

    const [filters, setFilters] = useState({
        search: "",
        order_code: "",
    });

    const [sellerStatuses, setSellerStatuses] = useState(DEFAULT_SELLER_STATUS);
    const [pitayaStatuses, setPitayaStatuses] = useState(DEFAULT_PITAYA_STATUS);

    const [form, setForm] = useState(null);

    const allowedSellerStatuses = useMemo(
        () => deriveSellerStatusesFromPitaya(pitayaStatuses),
        [pitayaStatuses]
    );

    const allowedPitayaStatuses = useMemo(
        () => derivePitayaStatusesFromSeller(sellerStatuses),
        [sellerStatuses]
    );

    const hasActiveFilters = useMemo(() => {
        return Boolean(
            filters.search?.trim() ||
            filters.order_code?.trim() ||
            sellerStatuses.length ||
            pitayaStatuses.length
        );
    }, [filters, sellerStatuses, pitayaStatuses]);

    useEffect(() => {
        const elements = document.querySelectorAll("html, body, #root, .app, .content");

        elements.forEach((el) => {
            if (!el) return;

            el.dataset.prevHeight = el.style.height || "";
            el.dataset.prevMinHeight = el.style.minHeight || "";

            el.style.height = "auto";
            el.style.minHeight = "100vh";
        });

        return () => {
            elements.forEach((el) => {
                if (!el) return;

                el.style.height = el.dataset.prevHeight || "";
                el.style.minHeight = el.dataset.prevMinHeight || "";
            });
        };
    }, []);

    function buildFirebaseProductImageUrl(fileName) {
        if (!fileName) return "";
        const cleanName = String(fileName).trim().replace(/^\/+/, "");
        const fullPath = `productapp/${cleanName}`;
        const encodedPath = encodeURIComponent(fullPath);

        return `https://firebasestorage.googleapis.com/v0/b/pitaya-pay-ebd8c.appspot.com/o/${encodedPath}?alt=media`;
    }

    function resolvePitayaThumb(item = {}) {
        if (!item?.code) return "";
        return buildFirebaseProductImageUrl(`${item.code}.jpg`);
    }

    function applySellerStatuses(nextSellerStatuses) {
        const normalizedSeller = uniqueArray(nextSellerStatuses);

        if (!normalizedSeller.length) {
            setSellerStatuses(DEFAULT_SELLER_STATUS);
            setPitayaStatuses(DEFAULT_PITAYA_STATUS);
            return;
        }

        const allowedPitaya = derivePitayaStatusesFromSeller(normalizedSeller);

        setSellerStatuses(normalizedSeller);
        setPitayaStatuses(allowedPitaya);
    }

    function applyPitayaStatuses(nextPitayaStatuses) {
        const normalizedPitaya = uniqueArray(nextPitayaStatuses);

        if (!normalizedPitaya.length) {
            setPitayaStatuses(DEFAULT_PITAYA_STATUS);
            setSellerStatuses(DEFAULT_SELLER_STATUS);
            return;
        }

        const allowedSeller = deriveSellerStatusesFromPitaya(normalizedPitaya);

        setPitayaStatuses(normalizedPitaya);
        setSellerStatuses(allowedSeller);
    }

    function toggleSellerStatus(status) {
        const next = sellerStatuses.includes(status)
            ? sellerStatuses.filter((item) => item !== status)
            : [...sellerStatuses, status];

        applySellerStatuses(next);
    }

    function togglePitayaStatus(status) {
        const next = pitayaStatuses.includes(status)
            ? pitayaStatuses.filter((item) => item !== status)
            : [...pitayaStatuses, status];

        applyPitayaStatuses(next);
    }

    function clearFilters() {
        setFilters({ search: "", order_code: "" });
        setSellerStatuses(DEFAULT_SELLER_STATUS);
        setPitayaStatuses(DEFAULT_PITAYA_STATUS);
        setTimeout(() => {
            loadOrders({ search: "", order_code: "", status: "", status_pitaya: "" });
        }, 0);
    }

    async function loadOrders(customFilters = null) {
        setLoadingList(true);

        const payload = {
            ...filters,
            ...(customFilters || {}),
            status: (customFilters?.status ?? sellerStatuses.join(",")) || "",
            status_pitaya: (customFilters?.status_pitaya ?? pitayaStatuses.join(",")) || "",
        };

        const res = await getManagerPersonalizedOrders(payload);

        if (res.ok) {
            const nextOrders = res.data || [];
            setOrders(nextOrders);

            if (nextOrders.length) {
                const stillExists = nextOrders.some((item) => item.id === selectedId);
                if (!selectedId || !stillExists) {
                    setSelectedId(nextOrders[0].id);
                }
            } else {
                setSelectedId(null);
                setDetail(null);
                setForm(null);
            }
        }

        setLoadingList(false);
    }

    async function loadDetail(id) {
        if (!id) return;

        setLoadingDetail(true);

        const [orderRes, paymentRes] = await Promise.all([
            getManagerPersonalizedOrderById(id),
            getManagerPersonalizedOrderPaymentSummary(id),
        ]);

        if (orderRes.ok && orderRes.data) {
            setDetail(orderRes.data);

            setForm({
                status: orderRes.data.status || "CREATED",
                status_pitaya: orderRes.data.status_pitaya || "CREATED",
                status_manual_override: Boolean(orderRes.data.status_manual_override),
                customer_name: orderRes.data.customer_name || "",
                customer_phone: orderRes.data.customer_phone || "",
                notes: orderRes.data.notes || "",
                items: Array.isArray(orderRes.data.items)
                    ? orderRes.data.items.map((item) => ({
                        id: item.id,
                        catalog_product: item.catalog_product || null,
                        catalog_product_id: item.catalog_product_id || null,
                        catalog_product_name: item.catalog_product_name || "",
                        catalog_product_data: item.catalog_product_data || null,
                        raw_item_payload: item.raw_item_payload || {},
                        code: item.code || "",
                        pitaya_code: item.pitaya_code || "",
                        supplier: item.supplier || "",
                        supplier_code: item.supplier_code || "",
                        product_name: item.product_name || "",
                        description: item.description || "",
                        product_type: item.product_type || "",
                        product_type_code: item.product_type_code || "",
                        color: item.color || "",
                        quantity: item.quantity || 1,
                        price: item.price ?? item.unit_price ?? 0,
                        line_total: item.line_total ?? item.total ?? 0,
                        custom_text: item.custom_text || "",
                        custom_texts: item.custom_texts || [],
                        selected_extra_option: item.selected_extra_option || "",
                        photo_url: item.photo_url || "",
                        photo_name: item.photo_name || "",
                        has_photo: Boolean(item.has_photo),
                    }))
                    : [],
            });
        }

        setPaymentSummary(paymentRes?.ok ? paymentRes.data : null);
        setLoadingDetail(false);
    }

    useEffect(() => {
        loadOrders();
    }, []);

    useEffect(() => {
        loadOrders();
    }, [sellerStatuses, pitayaStatuses]);

    useEffect(() => {
        if (selectedId) loadDetail(selectedId);
    }, [selectedId]);

    const selectedOrder = useMemo(
        () => orders.find((item) => item.id === selectedId) || null,
        [orders, selectedId]
    );

    function updateItemField(itemId, field, value) {
        setForm((prev) => ({
            ...prev,
            items: prev.items.map((item) =>
                item.id === itemId ? { ...item, [field]: value } : item
            ),
        }));
    }

    async function handleSave() {
        if (!selectedId || !form) return;

        setSaving(true);

        const payload = {
            status: form.status,
            status_pitaya: form.status_pitaya,
            status_manual_override: form.status_manual_override,
            customer_name: form.customer_name,
            customer_phone: form.customer_phone,
            notes: form.notes,
            items: form.items,
        };

        const res = await updateManagerPersonalizedOrder(selectedId, payload);

        setSaving(false);

        if (!res.ok) {
            await Swal.fire({
                icon: "error",
                title: "Erro ao salvar",
                text: res.message || "Não foi possível atualizar o pedido.",
                confirmButtonColor: "#902348",
            });
            return;
        }

        await Swal.fire({
            icon: "success",
            title: "Pedido atualizado",
            text: "As alterações foram salvas com sucesso.",
            confirmButtonColor: "#902348",
        });

        await loadOrders();
        await loadDetail(selectedId);
    }

    function handleSearchKeyDown(e) {
        if (e.key === "Enter") {
            e.preventDefault();
            loadOrders();
        }
    }

    function getSupplierCode(item = {}) {
        return (
            item?.supplier_code ||
            item?.catalog_product_data?.supplier_code ||
            item?.catalog_product?.supplier_code ||
            item?.raw_item_payload?.supplier_code ||
            "-"
        );
    }

    function handlePrintSingleItem(item) {
        if (!detail || !item) return;

        const items = form?.items || detail.items || [];

        const index = items.findIndex(
            (current) => current.id === item.id
        );

        const printItem = index >= 0 ? items[index] : item;

        const itemPosition = getItemPrintPosition(
            items,
            printItem
        );

        const html = buildItemPrintHtml({
            order: {
                ...detail,
                seller_name: detail?.seller_name,
                status_pitaya_label: detail?.status_pitaya_label,
            },
            item: printItem,
            includeSupplierHeader: true,
            itemPosition,
        });

        const suffix = itemPosition.repeated
            ? ` - Personalização ${itemPosition.position} de ${itemPosition.total}`
            : "";

        openPrintWindow({
            title: `Pedido ${detail.order_code} - ${getItemCode(printItem)}${suffix}`,
            bodyHtml: html,
        });
    }


    function parseNumber(value) {
        if (typeof value === "number") return Number.isFinite(value) ? value : 0;

        if (typeof value === "string") {
            const normalized = value.trim().replace(/\./g, "").replace(",", ".");
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : 0;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    function handleOpenSuccessPage() {
        if (!detail || !form) return;

        navigate("/produtos-personalizados/pedido-gerado", {
            state: {
                orderCode: detail.order_code || "",
                createdAt: detail.created_at || new Date().toISOString(),
                seller: {
                    display_name: detail.seller_name || "",
                    whatsapp: detail.seller_phone || detail.seller_whatsapp || "",
                    seller_id: detail.seller_id || "",
                    nome: detail.seller_name || "",
                },
                customer: {
                    name: form.customer_name || detail.customer_name || "",
                    phone: form.customer_phone || detail.customer_phone || "",
                },
                items: (form.items || []).map((item) => {
                    const quantity = Number(item.quantity || 1) || 1;

                    const unitPrice =
                        item.price != null && item.price !== ""
                            ? Number(item.price)
                            : item.line_total != null && item.line_total !== ""
                                ? Number(item.line_total) / quantity
                                : 0;

                    return {
                        optionId: item.id,
                        productName: item.product_name || item.catalog_product_name || "",
                        description: item.description || "",
                        customText: item.custom_text || "",
                        customTexts: item.custom_texts || "",
                        productType: item.product_type || "",
                        chainType: item.product_type || "",
                        color: item.color || "",
                        code: item.code || item.pitaya_code || "",
                        quantity,
                        price: unitPrice,
                        photoUrl: item.photo_url || "",
                        photoName: item.photo_name || "",
                    };
                }),
                total: Number(detail.total || 0),
                showSuccessModal: false,
            },
        });
    }
    function handlePrintGroupedBySupplier() {
        if (!detail || !Array.isArray(form?.items) || !form.items.length) return;

        const grouped = groupItemsBySupplier(form.items);
        const groups = Object.values(grouped);

        const html = groups
            .map((group) => {
                const itemsHtml = group.items
                    .map((item) => {
                        const itemPosition = getItemPrintPosition(
                            form.items,
                            item
                        );

                        return buildItemPrintHtml({
                            order: {
                                ...detail,
                                seller_name: detail?.seller_name,
                                status_pitaya_label: detail?.status_pitaya_label,
                            },
                            item,
                            includeSupplierHeader: false,
                            itemPosition,
                        });
                    })
                    .join("");

                const totalQuantity = group.items.reduce(
                    (sum, item) => sum + Number(item.quantity || 1),
                    0
                );

                return `
                    <section class="supplier-print-group">
                        <div style="
                            margin-bottom: 18px;
                            padding: 16px;
                            border-radius: 14px;
                            background: #f6f6f6;
                            border: 1px solid #ddd;
                        ">
                            <div style="font-size: 12px; color: #666;">
                                Fornecedor
                            </div>

                            <div style="font-size: 22px; font-weight: 800;">
                                ${escapeHtml(group.supplier)}
                            </div>

                            <div style="font-size: 14px; color: #444;">
                                ${escapeHtml(group.supplierCode)}
                            </div>

                            <div style="
                                font-size: 14px;
                                color: #444;
                                margin-top: 6px;
                            ">
                                Personalizações: ${group.items.length}
                                | Quantidade total: ${totalQuantity}
                            </div>
                        </div>

                        ${itemsHtml}
                    </section>
                `;
            })
            .join("");

        openPrintWindow({
            title: `Pedido ${detail.order_code} - Agrupado por fornecedor`,
            bodyHtml: html,
        });
    }

    return (
        <Box
            sx={{
                p: 2,
                pt: "60px",
                height: "100dvh",
                maxHeight: "100dvh",
                // overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
                minHeight: 0,
                overflow: 'auto',
                background:
                    "linear-gradient(180deg, #1A0D12 0%, #2E1420 18%, #472032 48%, #66304B 78%, #8A4063 100%)",
            }}
        >
            <Card
                sx={{
                    mb: 2,
                    flexShrink: 0,
                    borderRadius: 4,
                    boxShadow: "0 12px 28px rgba(0,0,0,0.30)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    background:
                        "linear-gradient(135deg, #12060A 0%, #2A0D17 30%, #54182F 68%, #7A2144 100%)",
                    backdropFilter: "blur(8px)",
                }}
            >
                <CardContent sx={{ p: 2.5 }}>
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        spacing={2}
                    >
                        <Box>
                            <Typography
                                variant="h4"
                                fontWeight={800}
                                sx={{ color: "#F3A6C3" }}
                            >
                                Gerencial Personalizados
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: "rgba(255, 214, 230, 0.78)" }}
                            >
                                Acompanhe, filtre e edite os pedidos personalizados.
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={() => loadOrders()}
                            sx={{
                                background: "linear-gradient(135deg, #A12C58 0%, #7A2144 100%)",
                                color: "#FFF4F8",
                                borderRadius: 2.5,
                                boxShadow: "none",
                                "&:hover": {
                                    background: "linear-gradient(135deg, #B73767 0%, #902348 100%)",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            Atualizar
                        </Button>
                    </Stack>

                    <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.10)" }} />

                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Busca geral"
                                value={filters.search}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                                }
                                placeholder="Cliente, revendedora, telefone, código"
                                onKeyDown={handleSearchKeyDown}
                                sx={darkFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Código"
                                value={filters.order_code}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, order_code: e.target.value }))
                                }
                                onKeyDown={handleSearchKeyDown}
                                sx={darkFieldSx}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                    variant="contained"
                                    onClick={() => loadOrders()}
                                    sx={{
                                        background: "linear-gradient(135deg, #A12C58 0%, #7A2144 100%)",
                                        color: "#FFF4F8",
                                        borderRadius: 2.5,
                                        boxShadow: "none",
                                        "&:hover": {
                                            background: "linear-gradient(135deg, #B73767 0%, #902348 100%)",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    Filtrar
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={clearFilters}
                                    sx={{
                                        borderRadius: 2.5,
                                        color: "#FFD6E6",
                                        borderColor: "rgba(255,255,255,0.18)",
                                        background:
                                            "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
                                        "&:hover": {
                                            borderColor: "rgba(255,255,255,0.28)",
                                            background:
                                                "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)",
                                        },
                                    }}
                                >
                                    Resetar
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Box mt={2}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            <Typography variant="caption" fontWeight={800} sx={{ color: "#F6B3CD" }}>
                                Status revendedora
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1}>
                            {SELLER_STATUS_OPTIONS.map((option) => {
                                const active = sellerStatuses.includes(option.value);
                                const disabled = !allowedSellerStatuses.includes(option.value);

                                return (
                                    <Chip
                                        key={option.value}
                                        label={option.label}
                                        clickable={!disabled}
                                        disabled={disabled}
                                        onClick={() => !disabled && toggleSellerStatus(option.value)}
                                        color={active ? getStatusChipColor(option.value) : "default"}
                                        variant={active ? "filled" : "outlined"}
                                        sx={{
                                            borderRadius: "10px",
                                            fontWeight: 700,
                                            opacity: disabled ? 0.42 : 1,
                                            color: active ? "#FFF4F8" : "#FFD6E6",
                                            border: active
                                                ? "1px solid rgba(255,255,255,0.10)"
                                                : "1px solid rgba(255,255,255,0.14)",
                                            background: active
                                                ? "linear-gradient(135deg, #A12C58 0%, #7A2144 100%)"
                                                : "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
                                            "& .MuiChip-label": {
                                                color: active ? "#FFF4F8" : "#FFD6E6",
                                            },
                                        }}
                                    />
                                );
                            })}
                            {hasActiveFilters && (
                                <IconButton
                                    size="small"
                                    onClick={clearFilters}
                                    sx={{
                                        color: "red",
                                        marginLeft: 1,
                                        border: "1px solid rgba(255,255,255,0.18)",
                                        background:
                                            "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                                        "&:hover": {
                                            color: "#FFFFFF",
                                            borderColor: "rgba(255,255,255,0.32)",
                                            background:
                                                "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.05) 100%)",
                                        },
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Stack>
                    </Box>

                    <Box mt={2}>
                        <Typography variant="caption" fontWeight={800} sx={{ color: "#F6B3CD" }}>
                            Status Pitaya
                        </Typography>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1}>
                            {PITAYA_STATUS_OPTIONS.map((option) => {
                                const active = pitayaStatuses.includes(option.value);
                                const disabled = !allowedPitayaStatuses.includes(option.value);

                                return (
                                    <Chip
                                        key={option.value}
                                        label={option.label}
                                        clickable={!disabled}
                                        disabled={disabled}
                                        onClick={() => !disabled && togglePitayaStatus(option.value)}
                                        variant={active ? "filled" : "outlined"}
                                        sx={{
                                            borderRadius: "10px",
                                            fontWeight: 700,
                                            opacity: disabled ? 0.42 : 1,
                                            color: active ? "#FFF4F8" : "#F3BCD1",
                                            border: active
                                                ? "1px solid rgba(255,255,255,0.10)"
                                                : "1px solid rgba(255,255,255,0.14)",
                                            background: active
                                                ? "linear-gradient(135deg, #8E2950 0%, #5E1834 100%)"
                                                : "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
                                            "& .MuiChip-label": {
                                                color: active ? "#FFF4F8" : "#F3BCD1",
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    </Box>
                </CardContent>
            </Card>

            <Grid
                container
                spacing={2}
                sx={{
                    flex: 1,
                    alignItems: "stretch",
                    // overflow: "hidden",
                }}
            >
                <Grid item xs={12} md={4} sx={{ display: "flex", maxHeight: "100vh" }}>
                    <Card
                        sx={{
                            flex: 1,
                            height: "100%",
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: 4,
                            boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            background:
                                "linear-gradient(180deg, #12060A 0%, #241019 42%, #3B1325 100%)",
                        }}
                    >
                        <CardContent sx={{ pb: 1 }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography variant="h6" fontWeight={800} sx={{ color: "#F3A6C3" }}>
                                    Pedidos
                                </Typography>

                                <Chip
                                    label={`${orders.length} resultado(s)`}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        color: "#FFF1F6",
                                        border: "1px solid rgba(255,255,255,0.10)",
                                        background:
                                            "linear-gradient(135deg, rgba(161,44,88,0.55) 0%, rgba(122,33,68,0.85) 100%)",
                                    }}
                                />
                            </Stack>
                        </CardContent>

                        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: "auto",
                                px: 2,
                                py: 1.5,
                            }}
                        >
                            {loadingList ? (
                                <Box display="flex" justifyContent="center" py={6}>
                                    <CircularProgress color="info" />
                                </Box>
                            ) : orders.length === 0 ? (
                                <Alert severity="info">Nenhum pedido encontrado.</Alert>
                            ) : (
                                <Stack spacing={1.25}>
                                    {orders.map((order) => (
                                        <Card
                                            key={order.id}
                                            onClick={() => setSelectedId(order.id)}
                                            sx={{
                                                cursor: "pointer",
                                                borderRadius: 3,
                                                boxShadow: "none",
                                                background:
                                                    selectedId === order.id
                                                        ? "linear-gradient(135deg, rgba(161,44,88,0.30) 0%, rgba(122,33,68,0.45) 100%)"
                                                        : "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
                                                border:
                                                    selectedId === order.id
                                                        ? "1.5px solid rgba(243,166,195,0.34)"
                                                        : "1px solid rgba(255,255,255,0.08)",
                                                transition: "all .18s ease",
                                                "&:hover": {
                                                    transform: "translateY(-1px)",
                                                    borderColor: "rgba(243,166,195,0.24)",
                                                    background:
                                                        "linear-gradient(135deg, rgba(161,44,88,0.18) 0%, rgba(122,33,68,0.28) 100%)",
                                                },
                                            }}
                                        >
                                            <CardContent sx={{ p: 1.6, "&:last-child": { pb: 1.6 } }}>
                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="flex-start"
                                                    spacing={1}
                                                    mb={0.75}
                                                >
                                                    <Box>
                                                        <Typography
                                                            fontWeight={800}
                                                            sx={{ color: "#FFD3E4" }}
                                                        >
                                                            {order.order_code}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ color: "rgba(255,220,232,0.66)" }}
                                                        >
                                                            {formatDateTime(order.created_at)}
                                                        </Typography>
                                                    </Box>

                                                    <Chip
                                                        size="small"
                                                        label={order.status_label || order.status}
                                                        color={getStatusChipColor(order.status)}
                                                    />
                                                </Stack>

                                                <Typography
                                                    variant="body2"
                                                    fontWeight={700}
                                                    sx={{ color: "#FFF2F7" }}
                                                >
                                                    {order.customer_name || "-"}
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: "rgba(255,220,232,0.78)" }}
                                                >
                                                    {order.seller_name || "-"}
                                                </Typography>

                                                <Stack
                                                    direction="row"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    mt={1}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: "rgba(255,220,232,0.66)" }}
                                                    >
                                                        {order.items_count} item(ns)
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={800}
                                                        sx={{ color: "#FFBDD7" }}
                                                    >
                                                        {formatBRL(order.total)}
                                                    </Typography>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8} sx={{ display: "flex" }}>
                    <Card
                        sx={{
                            flex: 1,
                            height: "100%",
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: 4,
                            boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            background:
                                "linear-gradient(180deg, #10060A 0%, #211019 40%, #351322 100%)",
                            overflow: "auto",
                            paddingRight: '10px'
                        }}
                    >
                        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", p: 2 }}>
                            {loadingDetail ? (
                                <Box display="flex" justifyContent="center" py={8} sx={{ marginTop: '40px' }} >
                                    <CircularProgress color="info" />
                                </Box>
                            ) : !detail || !form ? (
                                <Alert severity="info">Selecione um pedido.</Alert>
                            ) : (
                                <Stack spacing={2}>
                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: "flex-start", md: "center" }}
                                        spacing={1}
                                    >
                                        <Box>
                                            <Typography variant="h5" fontWeight={800} sx={{ color: "#F3A6C3" }}>
                                                {detail.order_code}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "rgba(255,220,232,0.72)" }}>
                                                Criado em {formatDateTime(detail.created_at)}
                                            </Typography>
                                        </Box>

                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            <Chip
                                                icon={<AssignmentIcon />}
                                                label={`Revendedora: ${detail.status_label || detail.status}`}
                                                color={getStatusChipColor(detail.status)}
                                                sx={{ padding: '10px' }}
                                            />
                                            <Chip
                                                icon={<LocalShippingIcon />}
                                                label={`Pitaya: ${detail.status_pitaya_label || detail.status_pitaya}`}
                                                variant="outlined"
                                                sx={{
                                                    color: "#FFF1F6",
                                                    borderColor: "rgba(255,255,255,0.14)",
                                                    background:
                                                        "linear-gradient(135deg, rgba(161,44,88,0.35) 0%, rgba(122,33,68,0.45) 100%)",
                                                    "& .MuiChip-icon": {
                                                        color: "#FFD6E6",
                                                    },
                                                    padding: '10px'
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <DetailInfoCard
                                                icon={<PersonOutlineIcon fontSize="small" />}
                                                title="Cliente"
                                                value={detail.customer_name}
                                                subtitle={detail.customer_phone || "Sem telefone"}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={3}>
                                            <DetailInfoCard
                                                icon={<ShoppingBagOutlinedIcon fontSize="small" />}
                                                title="Pedido"
                                                value={detail.order_code}
                                                subtitle={`${detail.items_count || 0} item(ns)`}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={3}>
                                            <DetailInfoCard
                                                icon={<Inventory2OutlinedIcon fontSize="small" />}
                                                title="Revendedora"
                                                value={detail.seller_name || "-"}
                                                subtitle={`Total ${formatBRL(detail.total)}`}
                                            />
                                        </Grid>


                                        <Grid item xs={12} sm={6} md={3}>
                                            <ItemCodesCard items={detail.items || []} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PaymentSummaryCard
                                                detail={detail}
                                                paymentSummary={paymentSummary}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Cliente"
                                                value={form.customer_name}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        customer_name: e.target.value,
                                                    }))
                                                }
                                                sx={darkFieldSx}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="WhatsApp"
                                                value={form.customer_phone}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        customer_phone: e.target.value,
                                                    }))
                                                }
                                                sx={darkFieldSx}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                select
                                                label="Status revendedora"
                                                value={form.status}
                                                disabled
                                                onChange={(e) =>
                                                    setForm((prev) => ({ ...prev, status: e.target.value }))
                                                }
                                                SelectProps={{ native: true }}
                                                sx={darkFieldSx}
                                            >
                                                {SELLER_STATUS_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                select
                                                label="Status Pitaya"
                                                value={form.status_pitaya}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        status_pitaya: e.target.value,
                                                    }))
                                                }
                                                SelectProps={{ native: true }}
                                                sx={darkFieldSx}
                                            >
                                                {PITAYA_STATUS_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {/* <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                select
                                                label="Override manual"
                                                disabled
                                                value={form.status_manual_override ? "1" : "0"}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        status_manual_override: e.target.value === "1",
                                                    }))
                                                }
                                                SelectProps={{ native: true }}
                                                sx={darkFieldSx}
                                            >
                                                <option value="0">Não</option>
                                                <option value="1">Sim</option>
                                            </TextField>
                                        </Grid> */}

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Observações internas"
                                                multiline
                                                minRows={3}
                                                value={form.notes}
                                                onChange={(e) =>
                                                    setForm((prev) => ({ ...prev, notes: e.target.value }))
                                                }
                                                sx={darkFieldSx}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

                                    <Box>
                                        <Typography
                                            variant="h6"
                                            fontWeight={800}
                                            mb={1.5}
                                            sx={{ color: "#F3A6C3" }}
                                        >
                                            Itens do pedido
                                        </Typography>

                                        <Stack spacing={1.2}>
                                            {detail.items?.map((item) => {
                                                console.log("ITEM ACCORDION", item);
                                                console.log("supplier_code", item?.supplier_code);
                                                console.log("supplier", item?.supplier);
                                                console.log("catalog_product", item?.catalog_product);
                                                console.log("raw_item_payload", item?.raw_item_payload);
                                                const formItem =
                                                    form.items.find((f) => f.id === item.id) || item;
                                                return (
                                                    <Accordion
                                                        key={item.id}
                                                        disableGutters
                                                        sx={{
                                                            borderRadius: "14px !important",
                                                            overflow: "hidden",
                                                            border: "1px solid rgba(255,255,255,0.08)",
                                                            boxShadow: "none",
                                                            background:
                                                                "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)",
                                                            "&:before": { display: "none" },
                                                        }}
                                                    >
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon sx={{ color: "#F3A6C3" }} />}
                                                            sx={{
                                                                background:
                                                                    "linear-gradient(135deg, rgba(161,44,88,0.16) 0%, rgba(122,33,68,0.28) 100%)",
                                                            }}
                                                        >
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                width="100%"
                                                                pr={2}
                                                                spacing={1.2}
                                                            >
                                                                <Stack direction="row" alignItems="center" spacing={1.2} sx={{ minWidth: 0, flex: 1 }}>
                                                                    <Box
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const img = resolvePitayaThumb(item);
                                                                            if (img) setPreviewImage(img);
                                                                        }}
                                                                        sx={{
                                                                            width: 32,
                                                                            height: 44,
                                                                            minWidth: 32,
                                                                            borderRadius: 2,
                                                                            overflow: "hidden",
                                                                            border: "1px solid rgba(255,255,255,0.10)",
                                                                            background: "rgba(255,255,255,0.05)",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            cursor: resolvePitayaThumb(item) ? "pointer" : "default",
                                                                        }}
                                                                    >
                                                                        {resolvePitayaThumb(item) ? (
                                                                            <Box
                                                                                component="img"
                                                                                src={resolvePitayaThumb(item)}
                                                                                alt={item?.product_name || "Produto"}
                                                                                sx={{
                                                                                    width: "100%",
                                                                                    height: "100%",
                                                                                    objectFit: "contain",
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <ImageOutlinedIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.45)" }} />
                                                                        )}
                                                                    </Box>

                                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.4 }}>
                                                                            <Chip
                                                                                size="small"
                                                                                label={`Fornecedor: ${getSupplierCode(item)}`}
                                                                                sx={{
                                                                                    fontWeight: 700,
                                                                                    color: "#FFF1F6",
                                                                                    border: "1px solid rgba(255,255,255,0.10)",
                                                                                    background:
                                                                                        "linear-gradient(135deg, rgba(161,44,88,0.35) 0%, rgba(122,33,68,0.55) 100%)",
                                                                                }}
                                                                            />
                                                                        </Stack>

                                                                        <Typography fontWeight={800} sx={{ color: "#FFD3E4" }} noWrap>
                                                                            <Box component="span" sx={{ color: "#F6B3CD", mr: 0.8 }}>
                                                                                {getItemCode(item)}
                                                                            </Box>
                                                                            {item.product_name}
                                                                        </Typography>

                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{ color: "rgba(255,220,232,0.74)" }}
                                                                            noWrap
                                                                        >
                                                                            {item.color || "-"} • {item.product_type || "-"}
                                                                        </Typography>
                                                                    </Box>
                                                                </Stack>

                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Chip size="small" label={`Qtd: ${item.quantity}`} />
                                                                    <Chip
                                                                        size="small"
                                                                        label={formatBRL(item.line_total)}
                                                                        variant="outlined"
                                                                        sx={{
                                                                            color: "#FFD6E6",
                                                                            borderColor: "rgba(255,255,255,0.14)",
                                                                            background:
                                                                                "linear-gradient(135deg, rgba(161,44,88,0.18) 0%, rgba(122,33,68,0.25) 100%)",
                                                                        }}
                                                                    />
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handlePrintSingleItem(item);
                                                                        }}
                                                                        sx={{
                                                                            color: "#FFD8E7",
                                                                            border: "1px solid rgba(255,255,255,0.10)",
                                                                            background:
                                                                                "linear-gradient(135deg, rgba(161,44,88,0.25) 0%, rgba(122,33,68,0.45) 100%)",
                                                                            "&:hover": {
                                                                                background:
                                                                                    "linear-gradient(135deg, rgba(161,44,88,0.35) 0%, rgba(122,33,68,0.60) 100%)",
                                                                            },
                                                                        }}
                                                                    >
                                                                        <PrintOutlinedIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Stack>
                                                            </Stack>
                                                        </AccordionSummary>

                                                        <AccordionDetails>
                                                            <Grid container spacing={2}>
                                                                <Grid item xs={12} md={6}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Descrição"
                                                                        value={formItem.description || ""}
                                                                        onChange={(e) =>
                                                                            updateItemField(
                                                                                item.id,
                                                                                "description",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        sx={darkFieldSx}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12} md={2}>
                                                                    <TextField
                                                                        fullWidth
                                                                        type="number"
                                                                        label="Quantidade"
                                                                        value={formItem.quantity || 1}
                                                                        onChange={(e) =>
                                                                            updateItemField(
                                                                                item.id,
                                                                                "quantity",
                                                                                Number(e.target.value || 1)
                                                                            )
                                                                        }
                                                                        sx={darkFieldSx}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12} md={4}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Opção extra"
                                                                        value={formItem.selected_extra_option || ""}
                                                                        onChange={(e) =>
                                                                            updateItemField(
                                                                                item.id,
                                                                                "selected_extra_option",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        sx={darkFieldSx}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Texto personalizado"
                                                                        multiline
                                                                        minRows={2}
                                                                        value={formItem.custom_text || ""}
                                                                        onChange={(e) =>
                                                                            updateItemField(
                                                                                item.id,
                                                                                "custom_text",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        sx={darkFieldSx}
                                                                    />
                                                                </Grid>

                                                                {!!item.custom_texts && (
                                                                    <Grid item xs={12}>
                                                                        <DetailsFieldsCard data={item.custom_texts} />
                                                                    </Grid>
                                                                )}

                                                                {item.photo_url ? (
                                                                    <Grid item xs={12}>
                                                                        <Card
                                                                            variant="outlined"
                                                                            sx={{
                                                                                borderRadius: 3,
                                                                                border: "1px solid rgba(255,255,255,0.08)",
                                                                                background:
                                                                                    "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 100%)",
                                                                                boxShadow: "none",
                                                                            }}
                                                                        >
                                                                            <CardContent
                                                                                sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                                                                            >
                                                                                <Stack spacing={1}>
                                                                                    <Typography
                                                                                        fontWeight={800}
                                                                                        sx={{ color: "#F3A6C3" }}
                                                                                    >
                                                                                        <PhotoCameraIcon
                                                                                            fontSize="inherit"
                                                                                            sx={{
                                                                                                mr: 0.5,
                                                                                                verticalAlign: "middle",
                                                                                            }}
                                                                                        />
                                                                                        Foto enviada
                                                                                    </Typography>

                                                                                    <Box
                                                                                        component="img"
                                                                                        src={item.photo_url}
                                                                                        alt={item.photo_name || item.product_name}
                                                                                        sx={{
                                                                                            width: 180,
                                                                                            maxWidth: "100%",
                                                                                            borderRadius: 2,
                                                                                            border: "1px solid rgba(255,255,255,0.10)",
                                                                                            background:
                                                                                                "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
                                                                                        }}
                                                                                    />
                                                                                </Stack>
                                                                            </CardContent>
                                                                        </Card>
                                                                    </Grid>
                                                                ) : null}
                                                            </Grid>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            })}
                                        </Stack>
                                    </Box>

                                    <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: "flex-start", md: "center", marginTop: 'auto' }}
                                        spacing={2}
                                    >
                                        <Box>
                                            <Typography fontWeight={800} sx={{ color: "#FFBDD7" }}>
                                                Total: {formatBRL(detail.total)}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "rgba(255,220,232,0.72)" }}
                                            >
                                                {detail.items_count} item(ns) • Revendedora:{" "}
                                                {detail.seller_name || "-"}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 2
                                            }}
                                        >
                                            <Button
                                                variant="outlined"
                                                startIcon={<PrintOutlinedIcon />}
                                                onClick={handlePrintGroupedBySupplier}
                                                disabled={!form?.items?.length}
                                                sx={{
                                                    borderRadius: 2.5,
                                                    color: "#FFD6E6",
                                                    borderColor: "rgba(255,255,255,0.18)",
                                                    "&:hover": {
                                                        borderColor: "#F3A6C3",
                                                        background: "rgba(255,255,255,0.05)",
                                                    },
                                                }}
                                            >
                                                Imprimir ordem de produção
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={handleOpenSuccessPage}
                                                sx={{
                                                    borderRadius: 999,
                                                    color: "#FFF1F6",
                                                    borderColor: "rgba(255,255,255,0.18)",
                                                    "&:hover": {
                                                        borderColor: "rgba(255,255,255,0.28)",
                                                        background: "rgba(255,255,255,0.05)",
                                                    },
                                                }}
                                            >
                                                Abrir Tela de final de compra
                                            </Button>
                                            <Button
                                                variant="contained"
                                                startIcon={<SaveIcon />}
                                                onClick={handleSave}
                                                disabled={saving}
                                                sx={{
                                                    background: "linear-gradient(135deg, #A12C58 0%, #7A2144 100%)",
                                                    color: "#FFF4F8",
                                                    minWidth: 180,
                                                    borderRadius: 2.5,
                                                    boxShadow: "none",
                                                    "&:hover": {
                                                        background: "linear-gradient(135deg, #B73767 0%, #902348 100%)",
                                                        boxShadow: "none",
                                                    },
                                                }}
                                            >
                                                {saving ? "Salvando..." : "Salvar alterações"}
                                            </Button>

                                        </Box>
                                    </Stack>
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Dialog
                open={Boolean(previewImage)}
                onClose={() => setPreviewImage("")}
                maxWidth="md"
                fullWidth
            >
                <Box
                    sx={{
                        p: 1,
                        background: "#12070B",
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        component="img"
                        src={previewImage}
                        alt="Preview"
                        sx={{
                            maxWidth: "100%",
                            maxHeight: "80vh",
                            objectFit: "contain",
                            borderRadius: 2,
                        }}
                    />
                </Box>
            </Dialog>
        </Box>
    );
}